import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/NotificationSubscriptionsService';
import {NotificationSubscription} from '../types';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';

export const useNotificationSubscription = (notificationSubscriptionId?: string) => {
    const queryClient = useQueryClient();

    const notificationSubscriptionQuery = useQuery({
        queryKey: ['notification-subscription', notificationSubscriptionId],
        queryFn: () => fetch(notificationSubscriptionId!),
        enabled: !!notificationSubscriptionId
    });

    const createMutation = useMutation({
        mutationFn: (values: NotificationSubscription) => {
            return add(stripReadonlyFields(values));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notification-subscriptions']});
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: NotificationSubscription) => {
            if (!notificationSubscriptionId) throw new Error('notificationSubscriptionId is required parameter');
            return update(notificationSubscriptionId, stripReadonlyFields(values) as NotificationSubscription);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notification-subscriptions']});
            queryClient.invalidateQueries({queryKey: ['notification-subscription', notificationSubscriptionId]});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!notificationSubscriptionId) throw new Error('notificationSubscriptionId is required parameter');
            return deleteRecord(notificationSubscriptionId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notification-subscriptions']});
        }
    });

    return {
        record: notificationSubscriptionQuery.data,
        loading: notificationSubscriptionQuery.isLoading,
        fetchRecord: notificationSubscriptionQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
